// SPDX-License-Identifier: Apache-2.0
import { AttributeTypeGroup } from './filter';

export const xmlNumberTypesStrList: string[] = [
  'byte',
  'decimal',
  'int',
  'integer',
  'long',
  'negativeInteger',
  'nonNegativeInteger',
  'nonPositiveInteger',
  'positiveInteger',
  'short',
  'unsignedLong',
  'unsignedInt',
  'unsignedShort',
  'unsignedByte',
  'double',
  'boolean'
] as const;

export const xmlStringTypesStrList: string[] = ['string'] as const;
export const xmlDatetimeTypesStrList: string[] = ['date', 'dateTime'] as const;
export const xmlGeometryTypesStrList: string[] = [
  'multipoint',
  'multilinestring',
  'multipolygon',
  'multisurface',
  'multicurve',
  'multigeometry',
  'point',
  'linestring',
  'polygon',
  'surface',
  'curve',
  'geometry',
  'unknown'
] as const;
export type XmlGeometryType = (typeof xmlGeometryTypesStrList)[number];

export const xmlTypesStrList = [
  ...xmlNumberTypesStrList,
  ...xmlStringTypesStrList,
  ...xmlDatetimeTypesStrList,
  ...xmlGeometryTypesStrList
] as const;
export type XmlTypes = (typeof xmlTypesStrList)[number];

export function isStringNumeric(str: string) {
  return str.trim().length > 0 && !Number.isNaN(Number(str)); // ensure strings of whitespace fail or mixed chars and numbers like '2n'
}

export function isString(attributeType: XmlTypes): boolean {
  return xmlStringTypesStrList.includes(attributeType);
}

export function isNumber(attributeType: XmlTypes): boolean {
  return xmlNumberTypesStrList.includes(attributeType);
}

export function isDate(attributeType: XmlTypes): boolean {
  return xmlDatetimeTypesStrList.includes(attributeType);
}

export function isGeometry(attributeType: XmlTypes): boolean {
  // Check for GML namespace
  return xmlGeometryTypesStrList.includes(attributeType) || attributeType.startsWith('gml:');
}

/**
 * Sanitizes the given geometry type by removing the 'gml:' prefix and simplifying it to one of the types
 * defined in xmlGeometryTypesStrList. Example: `gml:MultiLineStringPropertyType` -> `multilinestring`
 */
export function sanitizeGeometryType(attributeType: string): XmlGeometryType {
  const sanitizedType = attributeType.toLowerCase().replace('gml:', '');
  return xmlGeometryTypesStrList.find((type) => sanitizedType.startsWith(type)) ?? 'unknown';
}

/**
 * Checks if the geometry type can be considered area-like or is unknown, useful when doing `CONTAINS` comparisons.
 */
export function isAreaLikeGeometryTypeOrUnknown(attributeType: XmlGeometryType): boolean {
  return ['polygon', 'multipolygon', 'surface', 'multisurface', 'geometry', 'multigeometry', 'unknown'].includes(
    attributeType
  );
}

export function typeGroupFromAttributeType(attributeType: XmlTypes): AttributeTypeGroup {
  if (isString(attributeType)) {
    return 'string';
  } else if (isNumber(attributeType)) {
    return 'number';
  } else if (isDate(attributeType)) {
    return 'date';
  } else if (isGeometry(attributeType)) {
    return 'geometry';
  } else {
    throw new Error('Unknown attribute type');
  }
}
